import { AnchorProvider } from "@coral-xyz/anchor";
import { StructuredClient, VaultIdentifier } from "@katana-hq/sdk";
import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection } from "@solana/web3.js";
import { apyToApr, convert } from "@stepfinance/common";
import type { VaultPosition } from "@stepfinance/vaults";
import { useEffect, useState } from "react";
import useSWRImmutable from "swr/immutable";

import { useNetworkConfig } from "../../contexts/app/config";
import { useMarkets } from "../../contexts/market";
import { useRefreshContext } from "../../contexts/refresh";
import { swrFetcher } from "../../utils/swr";
import { getTokenByMint, getTokenBySymbol } from "../../utils/tokenInfo";
import type { ConnectedWallet } from "../wallet/useConnectedWallet";
import { useConnectedWallet } from "../wallet/useConnectedWallet";

export type KatanaOptionVaultPosition = VaultPosition & {
  apr: number;
  apy: number;
  fees: 0;
  key: string;
  platform: "Katana";
  pendingRewardUSD: 0;
  tokenSymbols: string[];
  userLiquidityA: number;
  userUSDValue: number;
  pendingWithdrawals?: number;
  vault: {
    url: string;
    tokenAMint: string;
    tokenBMint?: string;
    name: string;
  };
};

type KatanaOptionVaultAPYData = {
  call: {
    [symbol: string]: number;
  };
  put: {
    [symbol: string]: number;
  };
  vaultId: {
    [pubKey: string]: number;
  };
  all: {
    [vaultId: string]: {
      weeklyPerformance: number;
      apyWeekly: number;
    };
  };
};

const getVaultSymbolOverride = (vaultSymbol: string) => {
  if (vaultSymbol === "LUNA") {
    return "WLUNA";
  } else if (vaultSymbol === "INJ") {
    return "WINJ";
  } else if (vaultSymbol === "MSOL") {
    return "mSOL";
  }

  return vaultSymbol;
};

const getInfoSymbolOverride = (vaultSymbol: string) => {
  if (vaultSymbol === "ETH") {
    return "soETH";
  } else if (vaultSymbol === "STSOL") {
    return "stSOL";
  } else if (vaultSymbol === "CSOL") {
    return "cSOL";
  } else if (vaultSymbol === "INJ") {
    return "wINJ_v1";
  } else if (vaultSymbol === "MSOL") {
    return "mSOL";
  } else if (vaultSymbol === "WETH") {
    return "whETH";
  }

  return vaultSymbol;
};

export const useKatanaOptionVaults = () => {
  const { connection } = useConnection();
  const wallet = useConnectedWallet();
  const { refreshing } = useRefreshContext();
  const { network } = useNetworkConfig();
  const { marketsInitialised, priceInUSD } = useMarkets();
  const isMainnet = network === "mainnet-beta";
  const [userPositions, setUserPositions] = useState<KatanaOptionVaultPosition[]>([]);
  const {
    data: apyData = {
      all: {},
      call: {},
      put: {},
      vaultId: {},
    },
  } = useSWRImmutable<KatanaOptionVaultAPYData>(
    `https://raw.githubusercontent.com/Katana-Labs/statistics/master/apy/apy.json`,
    swrFetcher,
  );

  useEffect(() => {
    setUserPositions([]);
  }, [wallet]);

  const getUserPositions = async (
    wallet: ConnectedWallet,
    connection: Connection,
    priceInUSD: (mint: string) => number | null,
  ) => {
    const provider = new AnchorProvider(connection, wallet, { commitment: "confirmed" });
    const client = new StructuredClient(provider);
    const positions: KatanaOptionVaultPosition[] = [];

    for (const [callSymbol, callApy] of Object.entries(apyData.call)) {
      const apy = callApy * 100;
      const callSymbolOverride = getVaultSymbolOverride(callSymbol);
      client.attach(new VaultIdentifier("call", callSymbolOverride));
      const underlyingToken = getTokenBySymbol(getInfoSymbolOverride(callSymbol));
      if (!underlyingToken) {
        continue;
      }
      const deposit = convert(await client.getPendingDepositAmount(wallet.publicKey), underlyingToken.decimals);
      const shares = convert(await client.getTotalShares(wallet.publicKey), underlyingToken.decimals);
      // Descoping this for now. Would need an additional ticket for this to track other possible combination of states
      // your position could be in
      // const pendingWithdrawals = convert(
      //   await client.getTotalSharesPendingWithdrawal(wallet.publicKey),
      //   underlyingToken.decimals,
      // );
      const totalBalance = deposit + shares;

      if (totalBalance === 0) {
        continue;
      }
      positions.push({
        apr: apyToApr(apy, 52),
        apy: apy,
        fees: 0,
        key: `kov${callSymbol}call`,
        pendingRewardUSD: 0,
        platform: "Katana",
        tokenSymbols: [underlyingToken.symbol],
        userLiquidityA: totalBalance,
        userUSDValue: totalBalance * (priceInUSD(underlyingToken.address) ?? 0),
        vault: {
          name: `${callSymbol} Covered Call`,
          tokenAMint: underlyingToken.address,
          url: `https://app.katana.so/options/call/${callSymbolOverride.toLowerCase()}`,
        },
      });
    }
    const USDCTokenInfo = getTokenByMint("EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v")!;
    for (const [putSymbol, putApy] of Object.entries(apyData.put)) {
      const apy = putApy * 100;
      client.attach(new VaultIdentifier("put", putSymbol));
      const underlyingToken = getTokenBySymbol(getInfoSymbolOverride(putSymbol))!;
      if (!underlyingToken) {
        continue;
      }
      const deposit = convert(await client.getPendingDepositAmount(wallet.publicKey), USDCTokenInfo.decimals);
      const shares = convert(await client.getTotalShares(wallet.publicKey), USDCTokenInfo.decimals);
      // const pendingWithdrawals = convert(
      //   await client.getTotalSharesPendingWithdrawal(wallet.publicKey),
      //   underlyingToken.decimals,
      // );
      const totalBalance = deposit + shares;
      if (totalBalance === 0) {
        continue;
      }
      positions.push({
        apr: apyToApr(apy, 52),
        apy: apy,
        fees: 0,
        key: `kov${putSymbol}put`,
        pendingRewardUSD: 0,
        platform: "Katana",
        tokenSymbols: ["USDC"],
        userLiquidityA: totalBalance,
        userUSDValue: totalBalance * (priceInUSD(USDCTokenInfo.address) ?? 0),
        vault: {
          name: `${putSymbol} Put Sell`,
          tokenAMint: underlyingToken.address,
          tokenBMint: USDCTokenInfo.address,
          url: `https://app.katana.so/options/put/${putSymbol.toLowerCase()}`,
        },
      });
    }
    return positions;
  };

  useEffect(() => {
    if (wallet && connection && isMainnet && marketsInitialised && priceInUSD) {
      getUserPositions(wallet, connection, priceInUSD)
        .then(setUserPositions)
        .catch((err) => {
          console.error("Error fetching Katana Option Vault Positions:", err);
          captureException(err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet, connection, isMainnet, apyData, refreshing, marketsInitialised, priceInUSD]);

  return userPositions;
};
